import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ApiOutlined, DatabaseOutlined, SearchOutlined } from '@ant-design/icons';

import { boDau } from '../../../../components/layout/nav-search';
import { Badge, Button, Empty, Input, Select, Skeleton } from '../../../../components/ui';
import { useDuongDan } from '../../../../hooks/useDuongDan';
import { useT } from '../../../../i18n';
import { cn } from '../../../../lib/cn';
import { soVN } from '../../../../lib/dinh-dang';
import type { GraphqlSource } from '../../types';
import { ChonKieuHien, useKieuHien } from './KieuHien';
import { DaiLoi } from './VungDuLieu';

/**
 * Lưới thẻ nguồn — trang vào của Kho dữ liệu và Truy vấn mẫu (port `components/replica/LuoiNguon.tsx` native).
 *
 * Hai màn ấy trước đây đặt một dải nút nguồn ngang trên đầu: mười nguồn là tràn hai dòng, và mỗi nút không nói gì ngoài
 * tên. Thẻ cho mỗi nguồn đủ chỗ nói ra điều cần biết TRƯỚC khi bấm (loại, cách trả lời, tình trạng); việc thiết lập dồn
 * sang màn chi tiết của một nguồn.
 */

const SO_THE_CHO = 6;
const LUOI = 'grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4';

export interface BoLocNguon {
  /** Nhãn của ô chọn — cũng là `aria-label`. */
  placeholder: string;
  options: { value: string; label: string; khop: (s: GraphqlSource) => boolean }[];
}

export interface LuoiNguonProps {
  ds: GraphqlSource[];
  dangTai: boolean;
  loi: unknown;
  onThuLai: () => void;
  /** Đường dẫn GỐC (chưa có tiền tố cổng) của màn chi tiết một nguồn. */
  hrefCua: (s: GraphqlSource) => string;
  /** Khoá nhớ dạng lưới / dạng danh sách — mỗi màn một khoá (`KHOA_HIEN`). */
  khoaHien: string;
  boLoc: BoLocNguon[];
  /** Nhãn riêng của từng màn, nằm sau nhãn loại nguồn. */
  nhan?: (s: GraphqlSource) => ReactNode;
  /** Dòng tình trạng ở chân thẻ — `undefined` khi còn đang tải. */
  chan: (s: GraphqlSource) => ReactNode;
}

export function NhanLoaiNguon({ s }: { s: GraphqlSource }) {
  const { t } = useT();
  const laApi = s.kind === 'api';
  return (
    <Badge variant={laApi ? 'info' : 'neutral'}>
      {laApi
        ? t('troLyAi.luoiNguon.loaiApi')
        : s.kind === 'dsn'
          ? t('troLyAi.luoiNguon.loaiCsdl')
          : t('troLyAi.luoiNguon.loaiGraphql')}
    </Badge>
  );
}

function BieuTuongNguon({ s, nho }: { s: GraphqlSource; nho?: boolean }) {
  const laApi = s.kind === 'api';
  return (
    <span
      aria-hidden="true"
      className={cn(
        'flex shrink-0 items-center justify-center rounded-lg',
        nho ? 'size-7' : 'mt-0.5 size-8',
        laApi ? 'bg-sky-50 text-sky-600' : 'bg-primary-50 text-primary-600',
      )}
    >
      {laApi ? <ApiOutlined /> : <DatabaseOutlined />}
    </span>
  );
}

function TheNguon({ s, href, nhan, chan }: { s: GraphqlSource; href: string; nhan?: ReactNode; chan: ReactNode }) {
  return (
    <Link
      to={href}
      title={s.name}
      className="group flex flex-col gap-2 rounded-lg border border-default bg-surface p-4 shadow-sm transition-colors hover:border-primary-300 hover:bg-primary-50"
    >
      <div className="flex items-start gap-2.5">
        <BieuTuongNguon s={s} />
        <div className="min-w-0 flex-1">
          <p className="line-clamp-2 text-sm font-medium text-content-primary group-hover:text-primary-700">{s.name}</p>
          <p className="truncate text-xs text-content-muted">{s.moTa || s.id}</p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-1.5 text-xs">
        <NhanLoaiNguon s={s} />
        {nhan}
      </div>
      <div className="mt-auto flex items-center gap-1 pt-1 text-xs tabular-nums text-content-muted">
        {chan === undefined ? <Skeleton className="h-4 w-32" /> : chan}
      </div>
    </Link>
  );
}

/** Cùng thông tin như thẻ, xếp trên một dòng — đọc nhanh hơn khi có vài chục nguồn. */
function DongNguon({ s, href, nhan, chan }: { s: GraphqlSource; href: string; nhan?: ReactNode; chan: ReactNode }) {
  return (
    <li>
      <Link
        to={href}
        title={s.name}
        className="group flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 transition-colors hover:bg-primary-50"
      >
        <BieuTuongNguon s={s} nho />
        <div className="min-w-0 flex-1 basis-56">
          <p className="truncate text-sm font-medium text-content-primary group-hover:text-primary-700">{s.name}</p>
          <p className="truncate text-xs text-content-muted">{s.moTa || s.id}</p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-1.5 text-xs">
          <NhanLoaiNguon s={s} />
          {nhan}
        </div>
        <div className="flex w-56 shrink-0 items-center gap-1 text-xs tabular-nums text-content-muted">
          {chan === undefined ? <Skeleton className="h-4 w-32" /> : chan}
        </div>
      </Link>
    </li>
  );
}

export function LuoiNguon({ ds, dangTai, loi, onThuLai, hrefCua, khoaHien, boLoc, nhan, chan }: LuoiNguonProps) {
  const { t, locale } = useT();
  const duongDan = useDuongDan();
  const [kieu, datKieu] = useKieuHien(khoaHien);
  const [tuKhoaTho, setTuKhoa] = useState('');
  const [chon, setChon] = useState<string[]>(() => boLoc.map(() => ''));

  const tuKhoa = boDau(tuKhoaTho.trim());
  const dangLoc = tuKhoa !== '' || chon.some(Boolean);
  const loc = ds.filter(
    (s) =>
      boLoc.every((b, i) => {
        const v = chon[i];
        return !v || (b.options.find((o) => o.value === v)?.khop(s) ?? true);
      }) && (!tuKhoa || boDau(`${s.name} ${s.id} ${s.moTa ?? ''}`).includes(tuKhoa)),
  );

  return (
    <>
      <div className="flex flex-wrap items-center gap-2.5 rounded-lg border border-default bg-surface px-4 py-3 shadow-sm">
        <div className="min-w-[220px] flex-1">
          <Input
            allowClear
            aria-label={t('troLyAi.luoiNguon.timNguon')}
            prefix={<SearchOutlined className="text-primary-500" />}
            value={tuKhoaTho}
            onChange={(e) => setTuKhoa(e.target.value)}
            placeholder={t('troLyAi.luoiNguon.timNguon')}
          />
        </div>
        {boLoc.map((b, i) => (
          <Select<string>
            key={b.placeholder}
            allowClear
            aria-label={b.placeholder}
            placeholder={b.placeholder}
            value={chon[i] || undefined}
            onChange={(v) => setChon((c) => c.map((x, j) => (j === i ? (v ?? '') : x)))}
            options={b.options.map((o) => ({ value: o.value, label: o.label }))}
          />
        ))}
        <span className="text-xs tabular-nums text-content-muted">
          {t('troLyAi.luoiNguon.demNguon', { shown: soVN(loc.length, locale), total: soVN(ds.length, locale) })}
        </span>
        <ChonKieuHien value={kieu} onChange={datKieu} />
      </div>

      {loi ? (
        <div className="rounded-lg border border-default bg-surface">
          <DaiLoi loi={loi} onThuLai={onThuLai} />
        </div>
      ) : dangTai ? (
        <div className={LUOI} aria-busy="true">
          {Array.from({ length: SO_THE_CHO }, (_, i) => (
            <Skeleton key={i} className="h-36 rounded-lg" />
          ))}
        </div>
      ) : loc.length === 0 ? (
        <div className="rounded-lg border border-default bg-surface">
          {/* Rỗng vì bộ lọc: bỏ mô tả và nút — mời "thêm nguồn đầu tiên" khi người dùng vừa lọc hết là sai. */}
          {dangLoc && ds.length > 0 ? (
            <Empty title={t('troLyAi.luoiNguon.khongKhopTieuDe')} />
          ) : (
            <Empty
              icon={DatabaseOutlined}
              title={t('troLyAi.luoiNguon.rongTieuDe')}
              description={t('troLyAi.luoiNguon.rongMoTa')}
              action={
                <Link to={duongDan('/tro-ly-ai/ket-noi')}>
                  <Button variant="primary">{t('troLyAi.luoiNguon.sangKetNoi')}</Button>
                </Link>
              }
            />
          )}
        </div>
      ) : kieu === 'danh_sach' ? (
        <ul
          aria-label={t('troLyAi.luoiNguon.danhSachNguon')}
          className="divide-y divide-default overflow-hidden rounded-lg border border-default bg-surface shadow-sm"
        >
          {loc.map((s) => (
            <DongNguon key={s.id} s={s} href={duongDan(hrefCua(s))} nhan={nhan?.(s)} chan={chan(s)} />
          ))}
        </ul>
      ) : (
        <div className={LUOI}>
          {loc.map((s) => (
            <TheNguon key={s.id} s={s} href={duongDan(hrefCua(s))} nhan={nhan?.(s)} chan={chan(s)} />
          ))}
        </div>
      )}
    </>
  );
}
